import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { calculateBarHeight, formatCurrencyValue, getChartColorClass, getChartHexColor } from 'src/app/utils/format.utils';

export interface RevenueHistoryData {
  month: string;
  value: number;
}

@Component({
  selector: 'app-revenue-history-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './revenue-history-chart.component.html',
  styleUrls: ['./revenue-history-chart.component.scss']
})
export class RevenueHistoryChartComponent implements OnInit, OnChanges {
  @Input() data: RevenueHistoryData[] = [];
  @Input() title: string = 'Histórico de Faturamento';
  @Input() animated: boolean = true;
  
  // Controle do tipo de gráfico
  chartType: 'bar' | 'line' = 'bar';
  
  private maxValue: number = 100;
  public gridValues: number[] = [];
  public maxValueIndex: number = -1;
  
  constructor() { }
  
  ngOnInit(): void {
    this.calculateMaxValue();
    this.calculateGridValues();
    this.findMaxValueIndex();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.calculateMaxValue();
      this.calculateGridValues();
      this.findMaxValueIndex();
    }
  }

  /**
   * Alterna entre gráfico de barras e linha
   */
  toggleChartType(): void {
    this.chartType = this.chartType === 'bar' ? 'line' : 'bar';
  }
  
  /**
   * Calcula o valor máximo entre os itens do gráfico
   */
  private calculateMaxValue(): void {
    if (!this.data || this.data.length === 0) {
      this.maxValue = 100;
      return;
    }
    
    this.maxValue = Math.max(...this.data.map(item => item.value));
    
    // Adiciona uma margem de 10% para o gráfico não ficar no topo
    this.maxValue = Math.ceil(this.maxValue * 1.1);
  }
  
  /**
   * Calcula os valores do grid (linhas horizontais)
   */
  private calculateGridValues(): void {
    this.gridValues = [];
    const steps = 5;
    const stepValue = this.maxValue / steps;
    
    for (let i = 0; i <= steps; i++) {
      this.gridValues.push(Math.round(stepValue * i));
    }
    
    // Inverte para que o maior valor fique no topo
    this.gridValues.reverse();
  }

  /**
   * Encontra o índice do mês com maior faturamento
   */
  private findMaxValueIndex(): void {
    if (!this.data || this.data.length === 0) {
      this.maxValueIndex = -1;
      return;
    }

    let maxValue = this.data[0].value;
    this.maxValueIndex = 0;

    this.data.forEach((item, index) => {
      if (item.value > maxValue) {
        maxValue = item.value;
        this.maxValueIndex = index;
      }
    });
  }
  
  /**
   * Calcula a altura da barra como porcentagem
   */
  getBarHeight(value: number): string {
    if (this.maxValue === 0) return '0%';
    const percentage = (value / this.maxValue) * 100;
    return `${Math.max(1, percentage)}%`;
  }

  /**
   * Calcula a posição Y para o gráfico de linha (de baixo para cima)
   */
  getLineY(value: number): number {
    if (this.maxValue === 0) return 100;
    const percentage = (value / this.maxValue) * 100;
    return 100 - Math.max(1, percentage); // Inverte para que 0 seja embaixo
  }

  /**
   * Gera o path SVG para o gráfico de linha
   */
  getLinePath(): string {
    if (!this.data || this.data.length === 0) return '';
    
    const width = 100; // Largura total em porcentagem
    const stepX = width / (this.data.length - 1);
    
    let path = '';
    
    this.data.forEach((item, index) => {
      const x = index * stepX;
      const y = this.getLineY(item.value);
      
      if (index === 0) {
        path += `M ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
    });
    
    return path;
  }

  /**
   * Gera os pontos para o gráfico de linha
   */
  getLinePoints(): { x: number, y: number, value: number, month: string }[] {
    if (!this.data || this.data.length === 0) return [];
    
    const width = 100;
    const stepX = width / (this.data.length - 1);
    
    return this.data.map((item, index) => ({
      x: index * stepX,
      y: this.getLineY(item.value),
      value: item.value,
      month: item.month
    }));
  }
  
  /**
   * Formata valor monetário
   */
  formatValue(value: number): string {
    return formatCurrencyValue(value);
  }
  
  /**
   * Formata valor do grid
   */
  formatGridValue(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  }

  /**
   * Formata o mês para exibição
   */
  formatMonth(month: string): string {
    const [year, monthNum] = month.split('-');
    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];
    
    const monthIndex = parseInt(monthNum) - 1;
    return `${monthNames[monthIndex]}/${year.slice(2)}`;
  }
  
  /**
   * Obtém a classe de cor
   */
  getColorClass(index: number): string {
    return getChartColorClass(index);
  }

  /**
   * Obtém a cor hex
   */
  getHexColor(index: number): string {
    return getChartHexColor(index);
  }

  /**
   * Verifica se o índice corresponde ao mês com maior faturamento
   */
  isMaxValueMonth(index: number): boolean {
    return this.maxValueIndex === index;
  }
}
